/**
 * ChatMarkdown — renders Ask BLIP's replies.
 *
 * A deliberately small Markdown subset (headings, paragraphs, bold/italic,
 * inline code, links, bullet/numbered lists, tables, rules, quotes) turned
 * into React elements — never `dangerouslySetInnerHTML`, so model output can
 * never inject markup or script. Images are NOT rendered at all (a markdown
 * image is the classic way to leak data to a remote URL); links render only
 * for same-origin paths and http(s) URLs.
 */
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

function safeHref(
  href: string,
): { kind: 'internal' | 'external'; href: string } | null {
  const h = href.trim();
  // A backslash is read as "/" by browsers, so "/\evil.com" would be an
  // off-site link that looks internal. Refuse it (and control chars) outright.
  // eslint-disable-next-line no-control-regex -- deliberately matching control chars
  if (/[\\\u0000-\u001f]/.test(h)) return null;
  if (h.startsWith('/') && !h.startsWith('//'))
    return { kind: 'internal', href: h };
  if (/^https?:\/\//i.test(h)) {
    try {
      return { kind: 'external', href: new URL(h).toString() };
    } catch {
      return null;
    }
  }
  return null;
}

/** Hostname shown beside an external link, so a disguised link is visible. */
function hostOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Inline: `code`, **bold**, *em* / _em_, [text](href). Images become their alt text. */
function inline(text: string, key = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  const re =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(!?\[[^\]]*\]\([^)\s]+\))|(\*[^*\s][^*]*\*)|(_[^_\s][^_]*_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${key}-${n++}`;
    if (tok.startsWith('`')) {
      out.push(<code key={k}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      out.push(<strong key={k}>{inline(tok.slice(2, -2), k)}</strong>);
    } else if (tok.startsWith('[') || tok.startsWith('![')) {
      const lm = /^!?\[([^\]]*)\]\(([^)\s]+)\)$/.exec(tok);
      const label = lm?.[1] ?? '';
      const target = tok.startsWith('!') ? null : safeHref(lm?.[2] ?? '');
      if (!target) out.push(label);
      else if (target.kind === 'internal' && !target.href.startsWith('/api/'))
        out.push(
          <Link key={k} to={target.href}>
            {label}
          </Link>,
        );
      else if (target.kind === 'internal')
        out.push(label); // downloads are buttons, not links
      else
        // External links always show their real host (and the full URL on
        // hover), and send no referrer — text the model was fed could try to
        // dress a data-carrying URL up as an innocent label.
        out.push(
          <a
            key={k}
            href={target.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            referrerPolicy="no-referrer"
            title={target.href}
          >
            {label}
            <span className="md-host"> ({hostOf(target.href)})</span>
          </a>,
        );
    } else {
      out.push(<em key={k}>{inline(tok.slice(1, -1), k)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isDivider = (l: string) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(l);
const cells = (l: string) =>
  l
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

export function ChatMarkdown({ text }: { text: string }): JSX.Element {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let b = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const key = `b${b++}`;
    if (!line.trim()) {
      i += 1;
      continue;
    }
    // Fenced code
    if (/^```/.test(line)) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i]!))
        body.push(lines[i++]!);
      i += 1;
      blocks.push(
        <pre key={key} className="md-pre">
          {body.join('\n')}
        </pre>,
      );
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = Math.min(4, h[1]!.length + 1);
      const Tag = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5';
      blocks.push(
        <Tag key={key} className="md-h">
          {inline(h[2]!, key)}
        </Tag>,
      );
      i += 1;
      continue;
    }
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      blocks.push(<hr key={key} className="md-hr" />);
      i += 1;
      continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && isDivider(lines[i + 1]!)) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i]!))
        rows.push(cells(lines[i++]!));
      blocks.push(
        <div key={key} className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                {head.map((c, j) => (
                  <th key={j}>{inline(c, `${key}h${j}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(c, `${key}r${ri}c${j}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
        i += 1;
        // continuation lines (indented, not a new item)
        while (
          i < lines.length &&
          /^\s{2,}\S/.test(lines[i]!) &&
          !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i]!)
        ) {
          items[items.length - 1] += ` ${lines[i]!.trim()}`;
          i += 1;
        }
      }
      const List = ordered ? 'ol' : 'ul';
      blocks.push(
        <List key={key} className="md-list">
          {items.map((it, j) => (
            <li key={j}>{inline(it, `${key}l${j}`)}</li>
          ))}
        </List>,
      );
      continue;
    }
    if (/^\s*>/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i]!))
        q.push(lines[i++]!.replace(/^\s*>\s?/, ''));
      blocks.push(
        <blockquote key={key} className="md-quote">
          {inline(q.join(' '), key)}
        </blockquote>,
      );
      continue;
    }
    // Paragraph: consecutive plain lines
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !/^(#{1,4}\s|```|\s*([-*•]|\d+[.)])\s+|\s*>)/.test(lines[i]!) &&
      !(
        isTableRow(lines[i]!) &&
        i + 1 < lines.length &&
        isDivider(lines[i + 1]!)
      )
    ) {
      para.push(lines[i++]!);
    }
    // Never stall on a line no rule consumed.
    if (para.length === 0) para.push(lines[i++]!);
    blocks.push(
      <p key={key} className="md-p">
        {para.map((p, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            {inline(p, `${key}p${j}`)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return <div className="md">{blocks}</div>;
}
